import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle, Star, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

/**
 * PaymentResult — landed on by Cashfree's `returnUrl` after checkout.
 * We never trust the URL — the single source of truth is the backend,
 * which we poll until the webhook has marked the order paid.
 */
export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const orderId = params.get('order_id');
  const [state, setState] = useState('checking'); // checking | paid | failed | timeout | unknown
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!orderId) { setState('unknown'); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.status === 'paid') {
          setState('paid');
          refresh?.();
          return;
        }
        if (data.status && data.status !== 'created') {
          setState('failed');
          return;
        }
      } catch { /* ignore & retry */ }
      if (attempts < 20) setTimeout(tick, 1500);
      else setState('timeout');
    };
    tick();
    return () => { cancelled = true; };
  }, [orderId, refresh]);

  const goHome = () => navigate('/app');

  return (
    <div className="min-h-screen w-full bg-[#0B0B1A] text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-3xl border border-white/10 bg-gradient-to-br from-fuchsia-500/10 via-cyan-500/5 to-amber-500/10 p-6 text-center shadow-xl">
        {state === 'checking' && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-cyan-300" />
            <h1 className="mt-4 font-display font-bold text-xl">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-white/60">
              Hang tight — your bank is verifying. This usually takes a few seconds.
            </p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
            <h1 className="mt-4 font-display font-bold text-2xl">Payment successful!</h1>
            {details?.total_stars > 0 && (
              <p className="mt-2 text-amber-300 flex items-center justify-center gap-1 font-semibold text-lg">
                <Star size={16} fill="currentColor" /> +{details.total_stars.toLocaleString()} Stars added
              </p>
            )}
            <Button onClick={goHome} className="mt-6 w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full" data-testid="payment-result-continue">
              Back to WorldVibe <ArrowRight size={14} className="ml-1" />
            </Button>
          </>
        )}
        {state === 'failed' && (
          <>
            <AlertTriangle size={48} className="mx-auto text-rose-400" />
            <h1 className="mt-4 font-display font-bold text-xl">Payment was not completed</h1>
            <p className="mt-2 text-sm text-white/60">
              Your card or account was not charged. You can try again from the Top Up screen.
            </p>
            <Button onClick={goHome} className="mt-6 w-full" variant="outline">
              Go back
            </Button>
          </>
        )}
        {state === 'timeout' && (
          <>
            <Loader2 size={48} className="mx-auto text-amber-300" />
            <h1 className="mt-4 font-display font-bold text-xl">Still confirming…</h1>
            <p className="mt-2 text-sm text-white/60">
              Your bank is taking longer than usual. Your Stars will appear as soon as we get the confirmation — usually within a minute.
            </p>
            <Button onClick={goHome} className="mt-6 w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full">
              Return to app
            </Button>
          </>
        )}
        {state === 'unknown' && (
          <>
            <AlertTriangle size={48} className="mx-auto text-rose-400" />
            <h1 className="mt-4 font-display font-bold text-xl">Missing order</h1>
            <p className="mt-2 text-sm text-white/60">We couldn&apos;t find your order. Please return to WorldVibe.</p>
            <Link to="/app"><Button className="mt-6 w-full">Home</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
